import type { ShoppingItem, ShoppingList, ShoppingListTotals } from "@shared/types";

const round2 = (n: number) => Math.round(n * 100) / 100;

export function isDropped(item: ShoppingItem): boolean {
  return Boolean(item.droppedAt);
}

export function activeItems(list: ShoppingList): ShoppingList {
  return list.filter((i) => !isDropped(i));
}

/** Pre-tax price × quantity. */
export function lineSubtotal(item: Pick<ShoppingItem, "unitPrice" | "quantity">): number {
  return (Number(item.unitPrice) || 0) * (Number(item.quantity) || 0);
}

export function isTaxable(item: Pick<ShoppingItem, "taxable">): boolean {
  return item.taxable !== false;
}

/** What the line actually costs: subtotal + sales tax (if taxable) + shipping. */
export function itemCost(
  item: Pick<ShoppingItem, "unitPrice" | "quantity" | "shipping" | "taxable">,
  taxRate: number
): number {
  const sub = lineSubtotal(item);
  const tax = isTaxable(item) ? sub * ((Number(taxRate) || 0) / 100) : 0;
  return round2(sub + tax + (Number(item.shipping) || 0));
}

export function totals(list: ShoppingList, taxRate: number): ShoppingListTotals {
  let subtotal = 0;
  let shipping = 0;
  let planned = 0;
  let purchased = 0;
  for (const item of activeItems(list)) {
    const cost = itemCost(item, taxRate);
    subtotal += lineSubtotal(item);
    shipping += Number(item.shipping) || 0;
    planned += cost;
    if (item.status === "purchased") purchased += cost;
  }
  return {
    subtotal: round2(subtotal),
    tax: round2(planned - subtotal - shipping),
    shipping: round2(shipping),
    planned: round2(planned),
    purchased: round2(purchased),
    outstanding: round2(planned - purchased),
  };
}

/**
 * Plan category id → rolled-up materials from its active, priced line
 * items, rounded to whole dollars to match the Plan table. Unpriced items
 * don't count — adding a part before you know its price shouldn't zero
 * out the line — so categories with none are absent and callers fall back
 * to the manual figure.
 */
export function materialsByCategory(
  list: ShoppingList,
  taxRate: number
): Record<number, { total: number; count: number }> {
  const out: Record<number, { total: number; count: number }> = {};
  for (const item of activeItems(list)) {
    if (item.categoryId == null || lineSubtotal(item) <= 0) continue;
    const entry = out[item.categoryId] ?? (out[item.categoryId] = { total: 0, count: 0 });
    entry.total += itemCost(item, taxRate);
    entry.count += 1;
  }
  for (const entry of Object.values(out)) entry.total = Math.round(entry.total);
  return out;
}

export function newShoppingItem(overrides: Partial<ShoppingItem> = {}): ShoppingItem {
  return {
    id: `si_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`,
    name: "",
    link: "",
    unitPrice: 0,
    quantity: 1,
    shipping: 0,
    status: "outstanding",
    categoryId: null,
    notes: "",
    droppedAt: null,
    droppedReason: "",
    ...overrides,
  };
}

export interface ParsedNoteItem {
  name: string;
  unitPrice: number;
  quantity: number;
}

/**
 * Pulls priced items out of a budget-line note written as
 *   `Café 30" double wall oven $5,600 + KitchenAid cooktop $1,619 — remarks`
 *
 * Segments are split on " + "; anything after an em dash is commentary.
 * A leading `2×` sets quantity; the price is treated as the line total
 * unless followed by `ea`. Segments without a price are skipped.
 */
export function parseNoteItems(notes: string | undefined): ParsedNoteItem[] {
  if (!notes) return [];
  const body = notes.split(" — ")[0] ?? "";
  const items: ParsedNoteItem[] = [];
  for (const raw of body.split(/\s\+\s/)) {
    const segment = raw.trim();
    const m = segment.match(
      /^(?:(\d+)\s*[×x]\s+)?(.+?)\s+(?:@\s*)?~?\$([\d,]+(?:\.\d+)?)(\s*ea\b)?/i
    );
    if (!m || !m[2] || !m[3]) continue;
    const quantity = m[1] ? parseInt(m[1], 10) : 1;
    const price = parseFloat(m[3].replace(/,/g, ""));
    const each = Boolean(m[4]);
    items.push({
      name: m[2].trim(),
      quantity,
      unitPrice: each ? price : round2(price / quantity),
    });
  }
  return items;
}
